import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Users, TrendingUp } from "lucide-react";
import { IndianRupee } from "lucide-react";

interface AnalyticsSummaryCardsProps {
  totalRevenue: number;
  doctorName: string;
  doctorRevenue: number;
  averageDailyPatients: number;
  appointmentsCount: number;
  todayRevenue: number;
  monthlyRevenue?: number;
}

export function AnalyticsSummaryCards({
  totalRevenue,
  doctorName,
  doctorRevenue,
  averageDailyPatients,
  appointmentsCount,
  todayRevenue,
  monthlyRevenue
}: AnalyticsSummaryCardsProps) {
  return (
    <div className="grid gap-3 md:grid-cols-4">
      <Card className="transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer">
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Top row: Total Revenue */}
            <div className="flex items-center justify-between">
              {/* Left side: Icon and Title */}
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-blue-100">
                    <IndianRupee className="h-4 w-4 text-blue-800" />
                  </div>
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                </div>
              
              {/* Right side: Value */}
              <div className="text-2xl font-bold text-blue-800">
                ₹{(totalRevenue || 0).toLocaleString('en-IN')}
              </div>
            </div>

            {/* Bottom row: Monthly Revenue */}
            {monthlyRevenue !== undefined && (
              <div className="flex items-center justify-between">
                {/* Left side: Title - aligned with icon space */}
                <div className="flex items-center gap-2 ml-5">
                  <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                </div>
                
                {/* Right side: Value */}
                <div className="text-2xl font-bold text-blue-800">
                  ₹{(monthlyRevenue || 0).toLocaleString('en-IN')}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Card className="transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer">
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Top row: Icon and Title */}
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <TrendingUp className="h-4 w-4 text-green-800" />
              </div>
              <CardTitle className="text-sm font-medium">Today's Revenue</CardTitle>
            </div>
            
            {/* Bottom row: Value - aligned with the text */}
            <div className="text-2xl font-bold text-green-800 ml-10">
              ₹{(todayRevenue || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer">
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Top row: Top Doctor */}
            <div className="flex items-center justify-between">
              {/* Left side: Icon and Title */}
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-purple-100">
                  <Users className="h-4 w-4 text-purple-800" />
                </div>
                <CardTitle className="text-sm font-medium">Top Doctor</CardTitle>
              </div>
              
              {/* Right side: Empty space */}
              <div></div>
            </div>

            {/* Bottom row: Doctor Name */}
            <div className="flex items-center justify-between">
              {/* Left side: Empty space */}
              <div></div>
              
              {/* Right side: Value */}
              <div className="text-2xl font-bold text-purple-800">
                {doctorName || 'N/A'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="transition-all duration-200 hover:shadow-lg hover:scale-[1.02] cursor-pointer">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            {/* Left side: Icon and Title */}
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <Calendar className="h-4 w-4 text-orange-800" />
              </div>
              <CardTitle className="text-sm font-medium">Daily Patients</CardTitle>
            </div>
            
            {/* Right side: Value */}
            <div className="text-2xl font-bold text-orange-800">
              {(averageDailyPatients || 0).toFixed(1)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
